import * as Sentry from '@sentry/react'
import env from '../env.json'
import { errorCodes } from './errormessages'
import { INTERNAL_SERVER_ERROR, LOGOUT, NETWORK_ERROR, REFRESH_TOKEN_SUCCESS } from '../store/auth/AuthConstants'
import { store } from '../app/store'
import { sendCustomSentryError } from './sentryUtility'
import { safeLocalStorage } from '@quickmd-llc/frontend-utils'
import {
  describeRefreshError,
  isFatalRefreshError,
  isUnifiedRefreshEnabled,
  refreshSession,
} from '../api/refreshSession'

const baseHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Origin: '*',
}

// @ts-expect-error TS7034 -- MIGRATION: not approved, fix when encountered
let isFirstRequestFinished = null
// @ts-expect-error TS7034 -- MIGRATION: not approved, fix when encountered
let requestsForDelayList = []
let isTokenError = false

/**
 * How many times a single request may be replayed after a token refresh before we give
 * up and surface the 401. `attempt` has been threaded through this module since it was
 * written but was never read or incremented, so a refresh that kept returning
 * 'refreshed' against a token the backend kept rejecting could recurse without bound.
 */
const MAX_TOKEN_REPLAY_ATTEMPTS = 2

/**
 * The refresh endpoint, as `apiCall` receives it. Kept in one place because two
 * separate branches must recognise it: the delay queue at the top of `apiCall`, and
 * the 401 handler — which must NOT treat a 401 *from* this endpoint as a reason to
 * refresh, since that is the request it would be retrying.
 */
const REFRESH_PATH = 'api/v1/auth/refreshtoken'

/**
 * Strip record identifiers out of a request path before it goes to Sentry.
 *
 * The paths this breadcrumb describes interpolate real ids — `api/v1/patients/<guid>`,
 * `api/v1/visits/<guid>/complete`. A patient id is a HIPAA Safe-Harbor identifier, and a
 * breadcrumb is retained and searchable like any other Sentry payload, so shipping the raw
 * path would put PHI in a third-party system. The endpoint SHAPE is the whole diagnostic
 * value here; the id adds nothing we are allowed to keep.
 */
const redactIds = (api: string): string =>
  String(api)
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
    .replace(/\b\d{4,}\b/g, ':id')

/**
 * Detach a fire-and-forget `apiCall` from the unhandled-rejection channel.
 *
 * Nobody owns a post-refresh replay's promise: with `types` it dispatches its own
 * success/error and never rejects, and without `types` the original caller already
 * received `{ error: 'Cancel' }`. Left alone, a replay that hits
 * `MAX_TOKEN_REPLAY_ATTEMPTS` surfaces as a global unhandled rejection — a Sentry
 * event for something handled by design.
 *
 * Swallowing it silently would hide the one case worth knowing about: the cap firing
 * means the backend is rejecting a token it has just issued (clock skew, revoked
 * scope). So drop a breadcrumb instead of nothing — it rides along on whatever error
 * is reported next without being an error itself.
 *
 * `Promise.resolve` because `apiCall` returns `undefined` when it queues a request
 * on the delay list, and `.catch` on `undefined` would throw.
 */
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const swallow = (promise, context) =>
  void Promise.resolve(promise).catch((e) => {
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'warning',
      message: 'post-refresh replay failed',
      data: { ...context, api: redactIds(context?.api ?? ''), reason: describeRefreshError(e) },
    })
  })

/**
 * What a 2xx with no parseable body should resolve to.
 *
 * Every ordinary caller wants the `{ success: true }` sentinel. `refreshTokens` must not
 * get it: it classifies a non-null object with no `AccessToken` as "the server refused to
 * renew this session" and dispatches LOGOUT. An empty body, an HTML error page from a
 * proxy, or a stream that died mid-read are all transient infrastructure noise, not an
 * auth decision — `refreshSession` (the flag-ON path) already returns `transient-failed`
 * for exactly these. `undefined` fails both of `refreshTokens`' `typeof === 'object'`
 * guards, so the session survives and the next 401 refreshes again.
 */
const emptyOkBody = (api: string): { success: true } | undefined =>
  api === REFRESH_PATH ? undefined : { success: true }

export async function apiCall(
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  api,
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  method,
  auth = null,
  body = null,
  types = null,
  withBase = null,
  headers = null,
  attempt = 0,
  isBlob = false,
  isDelayRequest = false,
) {
  if (
    !isDelayRequest &&
    // @ts-expect-error TS7005 -- MIGRATION: not approved, fix when encountered
    isFirstRequestFinished === false &&
    api !== REFRESH_PATH &&
    store.getState().auth.loggedIn
  ) {
    requestsForDelayList.push({ api, method, auth, body, types, withBase, headers, attempt, isBlob })
    return
  }
  // @ts-expect-error TS7005 -- MIGRATION: not approved, fix when encountered
  if (isFirstRequestFinished === null) {
    isFirstRequestFinished = false
  }
  return new Promise((resolve, reject) => {
    let baseAPI = ''
    let requestHeaders = {}
    let response = ''
    if (withBase) {
      baseAPI = withBase
    } else {
      baseAPI = env.baseURL
    }
    if (!auth) {
      // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
      auth = { Authorization: `Bearer ${store.getState().auth.accessToken}` }
    }
    if (headers) {
      // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
      requestHeaders = { ...headers, ...auth }
    } else {
      // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
      requestHeaders = { ...baseHeaders, ...auth }
    }
    if (types && !store.getState().auth.isConnected) {
      isFirstRequestFinished = null
      setTimeout(() => {
        store.dispatch({
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          type: types.error,
          payload: { UserFriendlyErrorCode: errorCodes.NO_CONNECTION },
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          extraData: types.extraData,
        })
        store.dispatch({
          type: NETWORK_ERROR,
        })
      }, 600)
      return
    }
    fetch(baseAPI + api, { method, headers: requestHeaders, body })
      .then(async (res) => {
        // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
        response = res
        try {
          // const responseAPI = await res.json()
          if (isBlob && res.ok) {
            return res.blob()
          }
          if (res.ok && method === 'PUT') {
            const data = await res.json()
            if (data) {
              return { success: true, data }
            }
            return { success: true }
          }
          if (res.status === 401) {
            // A 401 *from the refresh endpoint itself* is an answer, not a trigger.
            // Falling into the refresh machinery below re-enters `refreshTokens` while
            // the outer refresh is still running: the `isRefreshingToken` guard defers
            // 1s, the `isRetry` arm then invokes the callback WITHOUT refreshing, that
            // callback re-issues this same POST with the same dead token, and the cycle
            // repeats once a second forever. Meanwhile the outer promise rejects with
            // `{ error: 'Cancel' }`, which `isFatalRefreshError` correctly reads as
            // non-fatal — so the provider is never logged out and sits in a zombie
            // session against a token the backend has already killed.
            //
            // Reject with an explicit shape rather than falling through to the shared
            // body parser below: a proxy or WAF can answer 401 with an HTML page, and
            // `JSON.parse` throwing there would surface a SyntaxError with no `status`,
            // which the classifier fails open on — back to the zombie session. With
            // `types` unset (the refresh call passes none) this reaches `reject(e)` at
            // the tail of the catch, so `refreshTokens` classifies `status: 401` as
            // fatal and ends the session once, with a reason tag.
            //
            // Latent today, not live: the endpoint is anonymous and reports a dead
            // session as `400 {"ErrorCode":"TokenNotValid"}` (verified against QA
            // 2026-08-05). This makes the client correct if that contract ever changes.
            if (api === REFRESH_PATH) {
              return Promise.reject({
                status: res.status,
                message: res.statusText,
                ErrorCode: 'Unauthorized',
              })
            }
            isTokenError = true
            try {
              // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
              if (Object.keys(auth).length !== 0) {
                if (!isUnifiedRefreshEnabled()) {
                  // Legacy path (flag OFF). It replayed with `attempt` unchanged, so the
                  // cap below never applied to it: a backend that keeps 401ing the
                  // freshly-rotated token (clock skew, a revoked scope) put the client in
                  // an unbounded refresh→replay→401 loop against the API. Same cap, same
                  // reason — the unified branch just happened to be the one that got it.
                  if (attempt >= MAX_TOKEN_REPLAY_ATTEMPTS) {
                    return Promise.reject({
                      status: res.status,
                      message: res.statusText,
                      ErrorCode: 'Unauthorized',
                    })
                  }
                  refreshTokens(() => {
                    // Fire-and-forget: with `types` the replay dispatches its own
                    // success/error and never rejects; without `types` the original
                    // caller already received `{ error: 'Cancel' }` below, so this
                    // promise is nobody's. Swallow it rather than let a capped replay
                    // surface as a global unhandled rejection (and a Sentry event).
                    swallow(apiCall(api, method, null, body, types, null, headers, attempt + 1, isBlob), {
                      api,
                      attempt: attempt + 1,
                      path: 'legacy',
                    })
                  })
                  return Promise.reject({ error: 'Cancel' })
                }
                if (attempt >= MAX_TOKEN_REPLAY_ATTEMPTS) {
                  // The token keeps being rejected after a "successful" refresh. Stop
                  // recursing and let the caller see the 401 rather than looping.
                  return Promise.reject({
                    status: res.status,
                    message: res.statusText,
                    ErrorCode: 'Unauthorized',
                  })
                }
                // Await the shared single-flight outcome so we retry ONLY on a real
                // token rotation and never leave the request silently cancelled on a
                // transient blip. This is the ONLY refresh path — see refreshTokens below.
                const outcome = await refreshSession()
                if (outcome === 'refreshed') {
                  // Token rotated — replay the original request (it dispatches its
                  // own success/error); cancel this now-superseded promise.
                  //
                  // `isDelayRequest` (10th arg) MUST be true. Without it the replay hits
                  // the delay guard at the top of `apiCall` — `isFirstRequestFinished` is
                  // still `false` at this point, because only the success handler flips
                  // it — so the replay is pushed onto `requestsForDelayList` instead of
                  // being sent. The reject below then lands in the catch, where the drain
                  // is gated on `!isTokenError` (already true), so nothing sends it and
                  // nothing clears the latch: every later `apiCall` queues forever.
                  // `withBase` must be forwarded too, or a replayed request that targeted
                  // a non-default host would silently retarget `env.baseURL`.
                  // Fire-and-forget — see the note on the legacy replay above.
                  swallow(apiCall(api, method, null, body, types, withBase, headers, attempt + 1, isBlob, true), {
                    api,
                    attempt: attempt + 1,
                    path: 'unified',
                  })
                  return Promise.reject({ error: 'Cancel' })
                }
                if (outcome === 'auth-rejected' || outcome === 'no-session') {
                  // 'auth-rejected': refreshSession already dispatched LOGOUT.
                  // 'no-session': the session was already gone before this request landed.
                  // Either way suppress this request's error — the redirect is the UX.
                  //
                  // Anything queued behind this 401 is dead mail: replaying it would fire
                  // requests against a token that no longer exists, on a tab that is already
                  // being redirected. Drop the queue and release the latch by hand — the
                  // `isTokenError` arm of the catch below deliberately leaves
                  // `isFirstRequestFinished` alone for the replay case, and there is no
                  // replay here.
                  requestsForDelayList = []
                  isTokenError = false
                  isFirstRequestFinished = null
                  return Promise.reject({ error: 'Cancel' })
                }
                // 'transient-failed': session intact but this request can't proceed.
                // Surface a deterministic connectivity error instead of stranding the
                // caller with no success/error action and no retry.
                //
                // Clearing `isTokenError` first is what keeps the delay queue alive. Only
                // the 'refreshed' branch above dispatches a replay, and only that replay
                // reaches the success handler that drains the queue and resets
                // `isFirstRequestFinished`. On this branch nothing was replayed, so leaving
                // the flag set sends the catch below into its no-drain arm: the queue keeps
                // whatever joined it while this 401 was in flight, `isFirstRequestFinished`
                // stays `false`, and from then on EVERY `apiCall` is pushed onto the queue
                // and returns `undefined` instead of being sent — the legacy API layer stops
                // working for the rest of the tab's life over one 5xx on the refresh
                // endpoint. Handing the queue to the `!isTokenError` drain replays it with
                // `isDelayRequest`, which is the path that actually sends.
                //
                // Clearing it in the catch's `isTokenError` arm instead is NOT equivalent:
                // that arm deliberately leaves `isFirstRequestFinished` alone because it
                // assumes an in-flight replay still owns it. On this branch there is no
                // replay, so the latch would never be released and the queue would never
                // drain.
                isTokenError = false
                return Promise.reject({
                  status: res.status,
                  message: res.statusText,
                  UserFriendlyErrorCode: errorCodes.NO_CONNECTION,
                })
              }
            } catch (e) {
              if (types) {
                // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
                store.dispatch({ type: types.error, payload: e })
              } else {
                return Promise.reject({ ErrorCode: 'Unauthorized' })
              }
            }
          }
          return res.text().then((text) =>
            res.ok ?
              text ? JSON.parse(text)
              : emptyOkBody(api)
            : text ?
              Promise.reject({
                // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
                ...JSON.parse(text),
                status: res.status,
                message: res.statusText,
              })
            : Promise.reject({
                status: res.status,
                message: res.statusText,
              }),
          )
        } catch (e) {
          return res.text().then((text) => (res.ok ? text : Promise.reject(text)))
        }
      })
      .then((response) => {
        // @ts-expect-error TS7005 -- MIGRATION: not approved, fix when encountered
        if (isFirstRequestFinished === false) {
          isFirstRequestFinished = true
        }
        setTimeout(() => {
          if (requestsForDelayList.length) {
            // @ts-expect-error TS7005 -- MIGRATION: not approved, fix when encountered
            const list = [...requestsForDelayList]
            requestsForDelayList = []
            list.forEach((config, idx, array) => {
              apiCall(
                config.api,
                config.method,
                // `null`, not `config.auth`. A queued request captured its auth object at
                // enqueue time; if a caller ever passes an explicit `Authorization` header,
                // replaying it verbatim would send the PRE-rotation token — the exact 401
                // this queue exists to avoid. Passing `null` makes the replay re-read the
                // rotated token from the store (see the `if (!auth)` default above). No
                // production caller passes explicit auth today, so this is latent; keep it
                // that way.
                null,
                config.body,
                config.types,
                config.withBase,
                config.headers,
                config.attempt,
                config.isBlob,
              )
              if (idx === array.length - 1) {
                isFirstRequestFinished = null
                isTokenError = false
              }
            })
          } else {
            isFirstRequestFinished = null
            isTokenError = false
          }
        }, 150)
        if (types) {
          store.dispatch({
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            type: types.success,
            payload: response,
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            extraData: types.extraData,
          })
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          if (types.callAPI) {
            setTimeout(() => {
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              types.callAPI()
            }, 800)
          }
        } else {
          resolve(response)
        }
      })
      .catch((e) => {
        console.log('Error e', e)
        sendCustomSentryError({
          // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
          message: e.message || 'An unknown error occurred',
          // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
          name: e.ErrorCode,
          // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
          status: e.status,
        })
        if (requestsForDelayList.length) {
          if (!isTokenError) {
            setTimeout(() => {
              // @ts-expect-error TS7005 -- MIGRATION: not approved, fix when encountered
              requestsForDelayList.forEach((config, idx, array) => {
                apiCall(
                  config.api,
                  config.method,
                  // `null` for the same reason as the success drain above — never replay a
                  // captured, possibly pre-rotation `Authorization` header.
                  null,
                  config.body,
                  config.types,
                  config.withBase,
                  config.headers,
                  config.attempt,
                  config.isBlob,
                  true,
                )
                if (idx === array.length - 1) {
                  isFirstRequestFinished = null
                  requestsForDelayList = []
                }
              })
            }, 150)
          } else {
            // A token error already replayed the originating request directly (see the
            // 401 branch), and the success handler will drain this queue. Clear the latch
            // regardless: leaving `isTokenError` stuck on means the NEXT failure that
            // finds a non-empty queue takes this same branch and never drains it, which
            // is how the client ends up refusing to send anything for the rest of the
            // session. `isFirstRequestFinished` is deliberately left alone here — the
            // in-flight replay still owns it.
            isTokenError = false
          }
        } else {
          isFirstRequestFinished = null
          // Nothing is queued and nothing is in flight, so no latch should survive.
          isTokenError = false
        }
        // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
        if (e.status === 512 || e.message === 'Failed to fetch') {
          if (types) {
            store.dispatch({
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              type: types.error,
              payload: { UserFriendlyErrorCode: 512 },
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              extraData: types.extraData,
            })
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            if (types.callAPI) {
              setTimeout(() => {
                // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
                types.callAPI()
              }, 800)
            }
          } else {
            reject(e)
          }
          return
        }
        // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
        if (e.message === 'Load failed') {
          // Every sibling branch guards on `types`; this one did not, so a no-`types`
          // caller hitting Safari's 'Load failed' dispatched `{ type: undefined }` from
          // inside the timer — redux throws, the timer swallows it, and this promise
          // NEVER settles. The legacy refresh below awaits exactly this call, so its
          // `finally` would never run and `isRefreshingToken` would stay latched,
          // blocking every future refresh in every tab.
          if (types) {
            setTimeout(() => {
              store.dispatch({
                // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
                type: types.error,
                payload: {
                  UserFriendlyErrorCode: errorCodes.NO_CONNECTION,
                  api,
                  body,
                },
                // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
                extraData: types.extraData,
              })
              store.dispatch({
                type: NETWORK_ERROR,
              })
            }, 600)
          } else {
            reject(e)
          }
          return
        }
        if (!navigator.onLine && types) {
          setTimeout(() => {
            store.dispatch({
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              type: types.error,
              payload: {
                UserFriendlyErrorCode: errorCodes.NO_CONNECTION,
                api,
                body,
              },
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              extraData: types.extraData,
            })
            store.dispatch({
              type: NETWORK_ERROR,
              payload: { api, body },
            })
          }, 600)
          return
        }
        // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
        if (response.status === 200) {
          if (types) {
            store.dispatch({
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              type: types.success,
              payload: { success: true },
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              extraData: types.extraData,
            })
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            if (types.callAPI) {
              setTimeout(() => {
                // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
                types.callAPI()
              }, 800)
            }
          } else {
            // We only reach a 200 inside the *catch* when parsing threw — an HTML error
            // page from a proxy, or a body that died mid-read. Same rule as the empty-body
            // case on the success path: see `emptyOkBody`.
            resolve(emptyOkBody(api))
          }
          return
        }
        // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
        if (types && e.error !== 'Cancel') {
          // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
          if (e.ErrorCode === 'InternalServerError') {
            store.dispatch({
              type: INTERNAL_SERVER_ERROR,
              // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
              payload: { ...e, api, body },
              // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
              extraData: types.extraData,
            })
          }
          store.dispatch({
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            type: types.error,
            // @ts-expect-error TS2698 -- MIGRATION: not approved, fix when encountered
            payload: { ...e, api, body },
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            extraData: types.extraData,
          })
        } else {
          reject(e)
        }
      })
  })
}

const JWTRefreshObject = () => {
  const jwt = store.getState().auth.accessToken
  if (jwt) {
    return {
      AccessToken: jwt,
    }
  }
}

/**
 * Refresh the session, then run `callBack` only if a new token was actually issued.
 *
 * With `unified_token_refresh` ON, delegates entirely to `refreshSession()`
 * (src/api/refreshSession.ts) — the same single-flight refresh the Orval layer uses,
 * so concurrent 401s from both HTTP layers collapse into ONE
 * `POST api/v1/auth/refreshtoken` instead of racing each other over a rotating token.
 *
 * With the flag OFF the legacy implementation below runs. It keeps its original shape —
 * the cross-tab `isRefreshingToken` guard, the 1s deferral, the 800ms callback delay —
 * but no longer decides logout by handing LOGOUT to `apiCall` as the refresh request's
 * `error` action type. That indirection was the bug: `apiCall` dispatches `types.error`
 * for every transient condition it knows about (the `auth.isConnected` flag, `Failed to
 * fetch`, Safari's `Load failed`, `!navigator.onLine`, any 5xx), so each one ended the
 * provider's session and reset `ChartsReducer`, losing an in-progress note — 706
 * `restclient-refresh-failed` Sentry events. Since the flag is OFF in production, that
 * is a live outage, not a dormant path.
 *
 * It now calls `apiCall` WITHOUT `types` so the promise actually settles (with `types`
 * set, every error branch dispatches and returns without ever resolving or rejecting),
 * and classifies the failure itself via `isFatalRefreshError`. Only a genuine auth
 * rejection — 401/403, or the 400 `TokenNotValid`/`CredentialsNotValid` this endpoint
 * really uses — dispatches LOGOUT. Both flag states are now safe to run.
 *
 * A 401 on this call reaches the `catch` below intact because `apiCall` exempts
 * `REFRESH_PATH` from its own 401→refresh trigger; without that exemption this function
 * re-enters itself once a second forever and never logs out. See the guard there.
 */
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const refreshTokens = async (callBack, isRetry = false) => {
  // Unified single-flight refresh: shared with the Orval layer so concurrent 401s
  // don't race two refreshers over the rotating token. Retries transient failures
  // and logs out only on a genuine auth rejection.
  if (isUnifiedRefreshEnabled()) {
    const outcome = await refreshSession()
    if (outcome === 'refreshed' && callBack) {
      callBack()
    }
    return
  }

  const isTokenRefreshing = safeLocalStorage.get('isRefreshingToken')
  safeLocalStorage.remove('isRefreshingToken')

  if (JWTRefreshObject()) {
    if (isTokenRefreshing) {
      setTimeout(() => {
        refreshTokens(callBack, true)
      }, 1000)
      return
    }
    if (isRetry) {
      callBack()
      return
    }
    try {
      safeLocalStorage.set('isRefreshingToken', JSON.stringify('true'))
      const refreshObject = JSON.stringify(JWTRefreshObject())
      const refreshEndPoint = REFRESH_PATH
      // No `types`: we need the outcome here to classify it. Passing `types` makes
      // apiCall dispatch and return on every error branch without settling, which is
      // why the old `catch` below was unreachable.
      const response = await apiCall(
        refreshEndPoint,
        'POST',
        null,
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        refreshObject,
        null,
        env.baseURL,
      )
      const accessToken =
        typeof response === 'object' && response !== null && 'AccessToken' in response ? response.AccessToken : null
      if (typeof accessToken === 'string' && accessToken.length > 0) {
        store.dispatch({ type: REFRESH_TOKEN_SUCCESS, payload: { AccessToken: accessToken } })
        // Legacy timing: apiCall fired `types.callAPI` 800ms after success. Callers
        // (the 401 replay in apiCall above) depend on the new token being in the store
        // first, so keep the same deferral rather than invoking synchronously.
        if (callBack) {
          setTimeout(callBack, 800)
        }
        return
      }
      // A 2xx whose *parsed body* carried no token means the server won't renew this
      // session. Anything else that resolved without a body (apiCall can resolve
      // `undefined` from its delay-queue path) is not a server decision — don't read it
      // as one, or a queue hiccup logs the provider out.
      if (typeof response === 'object' && response !== null) {
        store.dispatch({
          type: LOGOUT,
          reason: 'restclient-refresh-failed',
          logoutContext: { message: 'refresh succeeded with no AccessToken' },
        })
      }
    } catch (e) {
      // Only a genuine auth rejection ends the session. Network blips, 5xx and 429 leave
      // it intact — the originating request stays failed and the next 401 refreshes again.
      if (isFatalRefreshError(e)) {
        store.dispatch({
          type: LOGOUT,
          reason: 'restclient-refresh-failed',
          logoutContext: { message: describeRefreshError(e) },
        })
      }
    } finally {
      safeLocalStorage.remove('isRefreshingToken')
    }
  }
}
